import { type PaletteKey, PixelCanvas } from '../../lib';
import { grid, type Part, type RibbonKey } from '../body';
import type { Worn } from '../headwear';
import { DEAD_FACE, FACE } from '../looks';
import type { Outfit } from '../outfits';
import { drawnLater } from './art';

/**
 * The astronaut set (ADR-0112): a space helmet, a space suit and the Cosmos finish on the guns.
 *
 * The helmet is headwear tied on (`Worn`), not a hat that flies off: it moves with the head, so the glass stays over the
 * face in every frame (a hat lags the head by up to 4 px and would slide the glass off the face); it is the same round
 * bubble on every hairstyle (an afro or pigtails would poke out of the glass, or be cropped to nothing); and it stays on
 * the dead pose, so a downed astronaut still reads as one. Under the glass a comm cap (the "Snoopy cap") covers the
 * hair: a dark cap with the team's crown panel and an earcup, the face showing through the glass.
 *
 * Every part is procedural over ellipses (the glass, the cap, the ring), so the standing and lying helmet agree; the
 * suit's chest panel, ribs and pack are drawn on its body the same way. Team colours (`b B c`): the cap's crown, the
 * collar ring's stripe, the shoulder patches and, through `legs`, the boots.
 *
 * Rubric (`looks hat` / `looks outfit` sheets at 3× and 1:1 on every team, idle, run and dead; the wardrobe; a 2v2 at
 * 1280×720): Space helmet S5 R5 P4 L4 C4 A4 F4, Space suit S4 R4 P4 L4 C4 A4 F4, Cosmos R5 P5 F4. Still weak: alone in
 * the wardrobe the helmet's face is an outlined hole (it is drawn to show the face through it); the earcup reads as a
 * dark spot more than a headphone at 1:1; the suit's chest panel is mostly behind the gun.
 */

type Ellipse = { cx: number; cy: number; rx: number; ry: number };
/** Where (x, y) lies in `e`'s units (0 at its centre, 1 on its edge). */
const norm = (x: number, y: number, e: Ellipse): [number, number] => [(x - e.cx) / e.rx, (y - e.cy) / e.ry];
const inside = (x: number, y: number, e: Ellipse) => {
  const [u, v] = norm(x, y, e);
  return u * u + v * v <= 1;
};

/** Light on a dome at (u, v) of its outline: the sphere's normal toward a light top-left and in front (up to ~1). */
function sphereLit(u: number, v: number): number {
  const z = Math.sqrt(Math.max(0, 1 - u * u - v * v));
  return -0.55 * u - 0.6 * v + 0.58 * z;
}
/** A flat-ish volume lit from the top-left (-1.4 … 1.4). */
const lit = (u: number, v: number) => -u * 0.7 - v * 0.7;

// --- The space helmet ------------------------------------------------------------------------------------------------

/**
 * The glass at (u, v) of its bubble: a pale rim (the glass seen edge on), the light through it inside (`4`), a crescent
 * of light up its top-left (thickest where it faces the light), the bounce on the far inner rim, a glint top right.
 */
function glass(u: number, v: number): PaletteKey {
  const r = Math.sqrt(u * u + v * v);
  const deg = (Math.atan2(v, u) * 180) / Math.PI; // 0 right, -90 up
  const off = Math.abs(deg + 135);
  if (off < 55 && r > 0.9 - 0.12 * (1 - off / 55) && r < 0.97) return 'w';
  if (r > 0.88) return u + v > 0.9 ? '4' : '5';
  if (r > 0.8 && Math.abs(deg - 40) < 25) return '5';
  if (r > 0.72 && r < 0.86 && Math.abs(deg + 48) < 7) return 'w';
  return '4';
}

/** The comm cap at (x, y) on its dome `head`: the team's crown panel (a lit rim up its top-left), dark slate round it. */
function capTone(x: number, y: number, head: Ellipse, crown: Ellipse): PaletteKey {
  const l = sphereLit(...norm(x, y, head));
  if (inside(x, y, crown)) {
    const [cu, cv] = norm(x, y, crown);
    return l > 0.8 && cu * cu + cv * cv > 0.5 && cu + cv < 0 ? 'c' : l > 0.45 ? 'b' : 'B';
  }
  return l > 0.85 ? '3' : l > 0.35 ? '2' : '1';
}

/** An earcup at (u, v) of its oval: a rim lit top-left round a dark cushion. */
function earcup(u: number, v: number): PaletteKey {
  const r = u * u + v * v;
  return r > 0.55 ? (u + v < -0.3 ? '3' : '1') : u + v < -0.2 ? '2' : '1';
}

/**
 * The collar ring's front band at `u` round it (-1 left … 1 right), `d` px down from its lip: a lit metal lip, the
 * team's stripe, a dark underside, the right end turned from the light.
 */
function ringTone(u: number, d: number): PaletteKey {
  const right = u > 0.7;
  if (d < 1) return u < 0.2 ? 'w' : '5';
  if (d < 2) return right ? '3' : '4';
  if (d < 3) return right ? 'B' : u < 0 ? 'c' : 'b';
  if (d < 4) return right ? 'B' : 'b';
  return right ? '1' : '2';
}

/** The glass bubble round the whole head (pixel centres), resting in the collar ring. */
const BUBBLE: Ellipse = { cx: 30.5, cy: 27, rx: 26, ry: 21 };
/** The comm cap's dome, a little rounder than the face, and its crown panel. */
const HEAD: Ellipse = { cx: 30, cy: 29, rx: 20, ry: 18 };
const CROWN: Ellipse = { cx: 30, cy: 21, rx: 15, ry: 10 };
/** The collar ring: its top an ellipse round the neck, its front band `t` px deep under it. */
const RING = { cx: 30.5, cy: 40, rx: 20, ry: 4, t: 5 } as const;
const EARCUP: Ellipse = { cx: 17.5, cy: 35, rx: 4, ry: 5 };
/** How far down the cap comes at column x: to the brows at the front, over the ear and the nape at the back. */
const capLine = (x: number) => (x >= 24 ? 30 : 30 + (24 - x) * 2.5);

/**
 * The helmet on the head (in the hair's place, over the face; the frame's 64 × 52 from its top-left): the collar ring's
 * band in front, the earcup, the cap, the ring's top round the neck behind the chin, the glass round it all; the face
 * shows through where the cap leaves it, so every look's eyes, mouth, beard and glasses show; `alone` (the wardrobe's
 * picture, on nobody) has the glass there instead.
 */
function helmet(alone = false): Part {
  const c = new PixelCanvas(64, 52);
  for (let y = 0; y < c.h; y++) {
    for (let x = 0; x < c.w; x++) {
      const [px, py] = [x + 0.5, y + 0.5];
      const u = (px - RING.cx) / RING.rx;
      const d = Math.abs(u) <= 1 ? py - RING.cy - RING.ry * Math.sqrt(1 - u * u) : -1;
      const cap = inside(px, py, HEAD) && py < capLine(px);
      if (d >= 0 && d < RING.t) c.set(x, y, ringTone(u, d));
      else if (inside(px, py, EARCUP)) c.set(x, y, earcup(...norm(px, py, EARCUP)));
      else if (cap) c.set(x, y, capTone(px, py, HEAD, CROWN));
      else if (!alone && FACE[0].get(x - FACE[1], y - FACE[2]) !== null) continue;
      else if (inside(px, py, { ...RING })) c.set(x, y, u < -0.2 ? '5' : '4');
      else if (inside(px, py, BUBBLE)) c.set(x, y, glass(...norm(px, py, BUBBLE)));
    }
  }
  return [c, 0, 0];
}

/**
 * The helmet on the dead pose's head, seen from above (the chin left, the crown right): the bubble round the head, the
 * cap over the crown with an earcup on either side of it, the collar ring standing round the neck, lip toward the body.
 */
function deadHelmet(): Part {
  const [fx, fy] = [DEAD_FACE[1], DEAD_FACE[2]];
  const bubble: Ellipse = { cx: 42, cy: 55.5, rx: 19.5, ry: 14 };
  const head: Ellipse = { cx: 44, cy: 56.5, rx: 14, ry: 12 };
  const crown: Ellipse = { cx: 52, cy: 56.5, rx: 7, ry: 9 };
  const ring: Ellipse = { cx: 24.5, cy: 57, rx: 2.6, ry: 10 };
  const cups: Ellipse[] = [
    { cx: 44.5, cy: 47.5, rx: 3.5, ry: 2.5 },
    { cx: 44.5, cy: 66, rx: 3.5, ry: 2.5 },
  ];
  const c = new PixelCanvas(64, 72);
  for (let y = 0; y < c.h; y++) {
    for (let x = 0; x < c.w; x++) {
      const [px, py] = [x + 0.5, y + 0.5];
      const face = DEAD_FACE[0].get(x - fx, y - fy) !== null;
      const cup = face && cups.find((e) => inside(px, py, e));
      if (inside(px, py, ring)) {
        const [u, v] = norm(px, py, ring);
        c.set(x, y, ringTone(v * 0.9, (u + 1) * 2.5));
      } else if (cup) c.set(x, y, earcup(...norm(px, py, cup)));
      else if (face && px > 43 - ((py - 56.5) / 12) ** 2 * 5) c.set(x, y, capTone(px, py, head, crown));
      else if (face) continue;
      else if (inside(px, py, bubble)) c.set(x, y, glass(...norm(px, py, bubble)));
    }
  }
  return [c, 0, 0];
}

/** No tails: the helmet has nothing that trails (the team is on its crown and ring). */
const NONE: Part = [new PixelCanvas(1, 1), 0, 0];
const NO_TAILS: Record<RibbonKey, Part> = { hang: NONE, hang2: NONE, flowA: NONE, flowB: NONE, flowC: NONE };

const SPACE_HELMET = (): Worn => ({
  worn: true,
  front: helmet(),
  alone: helmet(true),
  ribbon: NO_TAILS,
  dead: deadHelmet(),
});

// --- The space suit --------------------------------------------------------------------------------------------------

/** White cloth lit from the top-left. */
const white = (l: number): PaletteKey => (l > 0.55 ? 'w' : l > -0.35 ? '5' : '4');

/**
 * The suit's standing top (the frame's x 2-53, y 34-55): the life-support pack peeking out behind the shoulders (left,
 * vents across it), a chunky white body, puffed shoulders with the team's patch on their outer side, the neck seal the
 * helmet's ring locks onto, a chest panel with three lights and a ribbed waist ring.
 */
function suit(): Part {
  const [x0, y0] = [2, 34];
  const c = new PixelCanvas(52, 22);
  const at = (x: number, y: number, k: PaletteKey) => c.set(x - x0, y - y0, k);
  for (let y = 35; y <= 54; y++) {
    for (let x = 3; x <= 12; x++) {
      if ((x === 3 || x === 12) && (y === 35 || y === 54)) continue;
      let k: PaletteKey = x === 3 || y === 35 ? '5' : x >= 11 || y === 54 ? '3' : '4';
      if (y === 35 && x > 3 && x < 9) k = 'w';
      if ((y === 38 || y === 41 || y === 44) && x >= 5 && x <= 9) k = '2';
      at(x, y, k);
    }
  }
  const body: Ellipse = { cx: 31, cy: 47, rx: 19, ry: 11 };
  for (let y = 44; y <= 55; y++) {
    for (let x = 13; x <= 48; x++) {
      if (y >= 52 && (x === 13 || x === 48)) continue;
      const [u, v] = norm(x + 0.5, y + 0.5, body);
      at(x, y, white(lit(u, v * 0.5)));
    }
  }
  for (const s of [
    { cx: 12.5, cy: 48, rx: 5.5, ry: 5, side: -1 },
    { cx: 49, cy: 48, rx: 5, ry: 5, side: 1 },
  ]) {
    for (let y = 42; y <= 54; y++) {
      for (let x = 6; x <= 55; x++) {
        if (!inside(x + 0.5, y + 0.5, s)) continue;
        const [u, v] = norm(x + 0.5, y + 0.5, s);
        const patch = Math.abs(u - s.side * 0.35) < 0.36 && v > -0.05 && v < 0.6;
        at(x, y, patch ? (u + v < -0.5 ? 'c' : u + v < 0.5 ? 'b' : 'B') : white(lit(u, v)));
      }
    }
  }
  for (let x = 19; x <= 43; x++) {
    const edge = x === 19 || x === 43;
    at(x, 44, edge ? '4' : x < 30 ? '5' : '4');
    at(x, 45, edge ? '3' : x < 30 ? '4' : '3');
  }
  for (let x = 14; x <= 47; x++) {
    at(x, 53, x % 2 ? '5' : '4');
    at(x, 54, x % 2 ? '4' : '3');
    at(x, 55, '3');
  }
  for (let y = 48; y <= 52; y++) {
    for (let x = 19; x <= 28; x++) at(x, y, y === 48 || x === 19 ? '3' : y === 52 || x === 28 ? '1' : '2');
  }
  for (const [x, k] of [
    [21, 'l'],
    [22, 'l'],
    [24, 'r'],
    [26, 'y'],
  ] as const)
    at(x, 50, k);
  return [c, x0, y0];
}

/** The suit lying (right of the legs, under the head): the ribbed waist ring, the white body, a shoulder either side. */
const DEAD_SUIT = grid(`
  ....55w.
  ...5wwww
  4355wwww
  535wwwww
  435wwwww
  535wwwww
  435wwww5
  535wwww5
  435wwww5
  535www55
  435www55
  535w5555
  43555555
  53555555
  4355555.
  53555554
  ....cb44
  ....bB4.
`);

/** White trousers shaded grey (lit edge `w`, cloth `5`, shade `4`) over moon boots in the team's colour. */
const SPACE_SUIT = (): Outfit => ({
  torso: suit(),
  dead: [DEAD_SUIT, 24, 51],
  legs: { c: 'w', b: '5', B: '4', n: 'c', N: 'b', m: 'c' },
});

// --- Cosmos ----------------------------------------------------------------------------------------------------------

/**
 * The Cosmos finish: a white hull (steel `3 4 5` up to `4 5 w`), deep navy where the wood was (`B`, its lit edge `b`)
 * and navy brass with a cyan glint (`1 B c`); the darkest steps (`k 1 2`, `N n`) stay dark for the outline.
 */
const COSMOS = {
  '3': '4',
  '4': '5',
  '5': 'w',
  N: 'k',
  n: '1',
  m: 'B',
  t: 'B',
  e: 'b',
  U: '1',
  Y: 'B',
  y: 'c',
} as const;

export const ASTRONAUT = drawnLater({ head: SPACE_HELMET, outfit: SPACE_SUIT, gun: COSMOS });
